import type { ComponentConfig } from "@puckeditor/core";
import { getWindowJobsiteConfig, type JobsiteBusinessContext } from "../../types/runtime-config";
import { Gallery, type GalleryProps } from "./gallery";
import { GalleryFields } from "./gallery.fields";
import { RegenerableTextField } from "../editor/RegenerableTextField";
import { ImagePicker } from "../editor/ImagePicker";
import { ProjectPicker } from "../editor/ProjectPicker";

export const GalleryConfig: ComponentConfig<GalleryProps> = {
  label: "Gallery",
  fields: {
    heading: {
      ...GalleryFields.heading,
      contentEditable: true,
      render: ({ value, onChange, field }) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="gallery.heading"
          label="Heading"
          multiline={false}
          fieldMeta={field?.metadata}
        />
      ),
    },
    subheading: {
      ...GalleryFields.subheading,
      contentEditable: true,
      render: ({ value, onChange, field }) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="gallery.subheading"
          label="Subheading"
          multiline={true}
          fieldMeta={field?.metadata}
        />
      ),
    },
    layout: GalleryFields.layout,
    variant: GalleryFields.variant,
    columns: GalleryFields.columns,
    showCaptions: GalleryFields.showCaptions,
    showTags: GalleryFields.showTags,
    images: {
      ...GalleryFields.images,
      arrayFields: {
        ...GalleryFields.images.arrayFields,
        sourceProjectId: {
          ...GalleryFields.images.arrayFields.sourceProjectId,
          render: ({
            value,
            onChange,
          }: {
            value?: string;
            onChange: (val: string | undefined) => void;
          }) => <ProjectPicker value={value || ""} onChange={onChange} label="Synced Project" />,
        },
        imageUrl: {
          ...GalleryFields.images.arrayFields.imageUrl,
          render: ({
            value,
            onChange,
          }: {
            value?: string;
            onChange: (val: string | undefined) => void;
          }) => <ImagePicker value={value || ""} onChange={onChange} label="Cover Image" />,
        },
        photos: {
          ...GalleryFields.images.arrayFields.photos,
          arrayFields: {
            ...GalleryFields.images.arrayFields.photos.arrayFields,
            imageUrl: {
              ...GalleryFields.images.arrayFields.photos.arrayFields.imageUrl,
              render: ({
                value,
                onChange,
              }: {
                value?: string;
                onChange: (val: string | undefined) => void;
              }) => <ImagePicker value={value || ""} onChange={onChange} label="Photo" />,
            },
          },
        },
        beforeImageUrl: {
          ...GalleryFields.images.arrayFields.beforeImageUrl,
          render: ({
            value,
            onChange,
          }: {
            value?: string;
            onChange: (val: string | undefined) => void;
          }) => <ImagePicker value={value || ""} onChange={onChange} label="Before Image" />,
        },
        afterImageUrl: {
          ...GalleryFields.images.arrayFields.afterImageUrl,
          render: ({
            value,
            onChange,
          }: {
            value?: string;
            onChange: (val: string | undefined) => void;
          }) => <ImagePicker value={value || ""} onChange={onChange} label="After Image" />,
        },
      },
    } as any,
    backgroundColor: GalleryFields.backgroundColor,
  },
  /**
   * A control that changes nothing teaches the pro to stop trusting the panel,
   * so every field is hidden in the layouts where the render ignores it.
   *
   * "before-after" is legacy: no longer offered for new galleries (the
   * BeforeAfter component replaced it) but saved rows keep rendering and stay
   * editable, so the option is re-added only while it is the saved value.
   */
  resolveFields: (data, { fields }) => {
    const layout = (data.props as { layout?: string })?.layout || "showcase";
    const isBeforeAfter = layout === "before-after";
    const resolved = { ...fields } as Record<string, any>;
    const images = resolved.images;

    if (isBeforeAfter) {
      const layoutField = resolved.layout;
      if (Array.isArray(layoutField?.options)) {
        resolved.layout = {
          ...layoutField,
          options: [
            ...layoutField.options,
            { label: "Before & After (legacy)", value: "before-after" },
          ],
        };
      }
      // The comparison card ignores the cards/editorial treatment entirely.
      delete resolved.variant;
    }

    if (!images?.arrayFields) {
      return resolved as typeof fields;
    }

    // The pair fields stay visible in every layout: a project with both
    // photos set shows a Before/After badge on its tile and opens as an
    // interactive comparison in the lightbox.
    const hidden = isBeforeAfter
      ? // No lightbox, no click-through, and the cover image, focus, and
        // project sync only affect tiles, none of which the comparison card
        // renders.
        ["photos", "clickAction", "href", "imageUrl", "position", "sourceProjectId"]
      : [];

    const arrayFields = { ...images.arrayFields };
    for (const key of hidden) {
      delete arrayFields[key];
    }
    if (layout === "masonry") {
      // Masonry images keep their own proportions, so there is no crop for
      // the focus setting to steer.
      delete arrayFields.position;
    }

    resolved.images = { ...images, arrayFields };
    return resolved as typeof fields;
  },
  defaultProps: {
    heading: "See Our Recent Work",
    subheading:
      "A quick look at finished projects, quality details, and the kind of results our customers see.",
    layout: "showcase",
    variant: "cards",
    columns: "3",
    showCaptions: "yes",
    showTags: "yes",
    images: [
      {
        imageUrl: "",
        alt: "Finished project",
        caption: "Recent completed project",
        tag: "Featured Work",
        beforeLabel: "Before",
        afterLabel: "After",
      },
      {
        imageUrl: "",
        alt: "Project detail",
        caption: "Craftsmanship close-up",
        tag: "Detail",
        beforeLabel: "Before",
        afterLabel: "After",
      },
      {
        imageUrl: "",
        alt: "Before and after result",
        caption: "Transformation result",
        tag: "Before & After",
        beforeLabel: "Before",
        afterLabel: "After",
      },
    ],
  },
  render: ({ id, puck, ...props }) => {
    const puckMetadata = puck?.metadata as
      | { business?: JobsiteBusinessContext; site?: { id?: string }; apiUrl?: string }
      | undefined;
    const windowConfig = getWindowJobsiteConfig();
    const business = puckMetadata?.business || windowConfig?.business;

    const resolvedProps = {
      ...props,
      // For synced-project pulls; metadata beats window config in the editor
      // iframes (window config lands after first paint there).
      puckSiteId: puckMetadata?.site?.id,
      puckApiUrl: puckMetadata?.apiUrl,
      heading:
        props.heading ||
        (business?.city ? `Recent Work in ${business.city}` : "See Our Recent Work"),
      subheading:
        props.subheading ||
        (business?.city
          ? `Browse real project photos from jobs completed in ${business.city} and nearby service areas.`
          : "Browse real project photos and recent completed work."),
      layout: props.layout || "showcase",
      columns: props.columns || "3",
      showCaptions: props.showCaptions || "yes",
      showTags: props.showTags || "yes",
    };

    return <Gallery id={id} {...resolvedProps} />;
  },
};
